<script lang="ts" setup>
  import * as Sentry from '@sentry/vue'
  import TitropSentry from '@/helpers/TitropSentry.ts'

  import { useErrorDialogService } from '@/services/ErrorDialogService'
  import ButtonDefault from '@/elements/ButtonDefault.vue'
  import { useRoute } from 'vue-router'
  import Accordion from 'primevue/accordion'
  import AccordionPanel from 'primevue/accordionpanel'
  import AccordionHeader from 'primevue/accordionheader'
  import AccordionContent from 'primevue/accordioncontent'

  const { visible, close, error } = useErrorDialogService()
  const currentRoute = useRoute()
  const routeDump = {
    fullPath: currentRoute.fullPath,
    name: currentRoute.name,
    params: currentRoute.params,
    query: currentRoute.query,
    hash: currentRoute.hash
  }

  const sentryId = ref()
  const errorMessage = computed(() => {
    let msg: string = error.value.error?.stack ?? '(unknown error)'
    if (__MODE__ === 'development') {
      const fileRegex = /https?:\/\/localhost:5173\/(src\/.*?\.vue)(\?[^:]*?)?:(\d+):(\d+)/g

      msg = msg.replace(fileRegex, (full, relativePath, _query, line, col) => {
        const file = `${relativePath}:${line}:${col}`
        return `<a class="text-blue-700 font-semibold cursor-pointer" onclick="fetch('/__open-in-editor?file=${encodeURIComponent(file)}')">${full}</a>`
      })
    }
    return msg
  })

  function ideLinks(txt?: string) {
    if (!txt || __MODE__ === 'production') return txt

    const fileRegex = new RegExp(/(.*\/(src.*?\.vue))/gi)
    return txt.replace(fileRegex, (full, _, relative) => {
      return `<a class="text-blue-700 font-semibold cursor-pointer" onclick="fetch('/__open-in-editor?file=${encodeURIComponent(`${relative}`)}')">${full}</a>`
    })
  }

  function startReport() {
    const opts = TitropSentry.ReportDialogOptions()
    Sentry.showReportDialog(opts)
  }

  const clipboard = useClipboard()
  let stopEventGetter = false

  function onClose() {
    stopEventGetter = true
  }

  function onOpen() {
    sentryId.value = undefined
    stopEventGetter = false
    const eventGetter = () => {
      if (stopEventGetter) return
      sentryId.value = Sentry.lastEventId()
      if (!sentryId.value) setTimeout(eventGetter, 250)
    }
    setTimeout(eventGetter, 250)
  }
</script>
<template>
  <Dialog
    v-model:open-state="visible"
    v-model:visible="visible"
    :style="{ 'max-width': '100vw' }"
    maximizable
    modal
    @close="onClose"
    @show="onOpen">
    <template #header>
      <h1 class="w-full text-center mt-4 text-3xl font-bold tracking-tight text-red-600 sm:text-3xl">Es ist ein Fehler aufgetreten</h1>
    </template>
    <div class="p-error-content">
      <p class="mt-2 text-base leading-7 text-gray-600 text-center">
        Bitte versuchen Sie erneut, die letzte Aktion auszuführen.
        <br />
        Sollte der Fehler weiterhin bestehen bleiben, wenden Sie sich an den Administrator.
      </p>

      <p
        v-if="TitropSentry.Enabled && sentryId"
        class="mt-2 text-base leading-7 text-gray-600 text-center">
        Der Fehler wurde automatisch unter der Event-Id
        <span
          class="font-semibold"
          @dblclick="clipboard.copy(sentryId)">
          {{ sentryId }}
        </span>
        registriert.
        <br />
        Zusätzlich können Sie auch
        <ButtonDefault
          class="px-1 py-0"
          label="einen Fehlerbericht"
          size="small"
          @click="startReport()" />
        anfügen.
      </p>
      <Accordion
        :active-index="0"
        class="w-auto mx-auto mt-4 rounded-md">
        <AccordionPanel :value="0">
          <AccordionHeader><h3 class="text-sm font-semibold">Fehlerdetails</h3></AccordionHeader>
          <AccordionContent>
            <div class="ml-3 text-left">
              <div class="mt-2 text-sm whitespace-pre overflow-x-auto">
                <div class="mb-2">
                  <div class="font-semibold">Exception:</div>
                  <div v-html="errorMessage" />
                </div>
                <div class="mb-2">
                  <div class="font-semibold">Routing:</div>
                  <div>{{ routeDump }}</div>
                </div>
                <div
                  v-if="error.component"
                  class="mb-2">
                  <div class="font-semibold">Related component:</div>
                  <div>
                    ( {{ error.component.$?.type?.__name ?? '(unknown)' }} (
                    <!--                                    <span v-titrop-html="ideLinks(component?.$type?.__file ?? component?.$?.type?.__file)"></span>-->
                    <span v-html="ideLinks(error.component?.$?.type?.__file)" />
                    )
                  </div>
                </div>
              </div>
            </div>
          </AccordionContent>
        </AccordionPanel>
        <AccordionPanel
          v-if="error.axios"
          :value="1">
          <AccordionHeader><h3 class="text-sm font-semibold">XHR request</h3></AccordionHeader>
          <AccordionContent>
            <div class="mb-2">
              <div class="font-semibold">Axios summary:</div>
              <div>
                {{ `[${error.axios.method}] ${error.axios.url}` }}
              </div>
              <div>{{ error.axios?.message }}</div>
            </div>
            <Accordion multiple>
              <AccordionTab
                content-class="pt-0"
                header="Original request config">
                <div class="border-1">
                  {{ error.axios?.config }}
                </div>
              </AccordionTab>
              <AccordionTab
                content-class="pt-0"
                header="Original response frame">
                <div class="border-1">
                  {{ error.axios.response }}
                </div>
              </AccordionTab>
            </Accordion>
          </AccordionContent>
        </AccordionPanel>
      </Accordion>
    </div>

    <template #footer>
      <Button
        label="Schließen"
        @click="close" />
    </template>
  </Dialog>
</template>
<style>
  .sentry-error-embed-wrapper {
    z-index: 2000 !important;
  }
</style>
